import type { QueryOptions } from 'apollo-client';
import type { DocumentNode } from 'graphql';
import gql from 'graphql-tag';

import { getSSRFeatureFlag } from '@confluence/ssr-utilities';

import { getOperationName, UNEXPECTED_RESPONSE } from './getOperationName';

function getQueryName(operationName: string | null | undefined, variables: any) {
	if (!operationName) return '';

	let name = operationName;
	if (operationName === 'WebItemLocationQuery' || operationName === 'WebPanelLocationQuery') {
		name = `${operationName}:${
			variables?.location || (variables?.locations || []).join(',') || ''
		}`;
	}
	return name.replace(/[^\-\.\w]/g, '_');
}

/**
 * This is a lightweight graphql client.
 * If the Apollo client is presented in the global space then we will pass the query to Apollo client.
 * Otherwise we use fetch function and save the response in @confluence/query-preloader
 */
export function query<Q, V extends {}>({
	query,
	variables,
	fetchPolicy = 'cache-first',
	errorPolicy = 'all',
	context,
}: QueryOptions<V>): Promise<{ data: Q | null }> {
	const apolloClient = window.GLOBAL_APOLLO_CLIENT;
	if (!apolloClient) {
		// Referencing to the client via global variable to avoid circular dependency
		// This should never happen and will be removed in the future
		return Promise.resolve(UNEXPECTED_RESPONSE);
	}
	if (typeof query === 'string') {
		// For backward compatibility. We shouldn't really doing this.
		query = gql(query);
	}

	const name = getOperationName(query);

	if (
		process.env.NODE_ENV !== 'production' &&
		// This is indirect reference to next/packages/graphql/src/links/DevOnlyGraphqlErrorGenerator.ts
		(window as any).graphqlOperationController
	) {
		const operationController = (window as any).graphqlOperationController;
		const { shouldGenerateError } = operationController.onOperationRequested(name);
		if (shouldGenerateError) {
			return Promise.resolve(UNEXPECTED_RESPONSE);
		}
	}

	let single = true;
	if (process.env.REACT_SSR) {
		const queryName = getQueryName(name, variables);
		const httpBatchConfig = getSSRFeatureFlag('confluence.ssr.http.batch') || {};
		if (httpBatchConfig.enabled && queryName in (window?.__SSR_BATCH_QUERIES__ || {})) {
			single = false;
		}
	}

	return apolloClient.query({
		query,
		variables,
		errorPolicy,
		fetchPolicy,
		context: {
			single,
			...context,
		},
	});
}

export function writeQuery<TData, TVariable>(options: {
	query: DocumentNode;
	variables: TVariable;
	data: TData;
}) {
	const apolloClient = window.GLOBAL_APOLLO_CLIENT;
	if (apolloClient) {
		apolloClient.writeQuery(options);
	}
}
